import { useState, useEffect } from "react";
import axiosInstance from "../../../Fbaseurl";
import { useNavigate, useLocation } from "react-router-dom";
import backNav from "../../../assets/backNav.svg";
import PDF from "../../../assets/PDF.svg";
import CSV from "../../../assets/CSV.svg";
import canaryLogo from "../../../assets/Logo.svg";
import { useDispatch, useSelector } from "react-redux";
import html2pdf from "html2pdf.js";
import { showToast } from "../../../store/reducers/ToastMessageReducer";
import { updateSelectedCompnies, updateCompanies } from "../../../store/actions/companyAction";
import Header from "../../components/CommonComponents/Header";
import { useMediaQuery } from "@mui/material";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import "./index.css";
import {
  convertJsonToExcel,
  prepareRequiredJsonToExcelDownload,
  resolveVersionHeading,
  buildVersionedFileName,
} from "../../SalesPersonCommonMethods/taxCreditsReport";

// Helper: Extract projected credit and eligible credits
function extractTaxCreditFields(post) {
  let projectedCredit, eligibleCredits = [];
  if (
    typeof post.projectedCredit !== "undefined" &&
    typeof post.eligibleCredits !== "undefined"
  ) {
    return {
      projectedCredit: post.projectedCredit,
      eligibleCredits: post.eligibleCredits,
    };
  }
  if (Array.isArray(post.taxCredits) && post.taxCredits.length > 0) {
    projectedCredit = post.taxCredits[0]?.totalProjectedTaxCreditAmount;
    eligibleCredits = post.taxCredits[0]?.availableTaxCredits?.map((c) => c.name) || [];
  }
  return { projectedCredit, eligibleCredits };
}

const TaxCreditAnalysis = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dataFetchStatus, setDataFetchStatus] = useState(""); // "TRIGGERED", "FETCHED_SUCCESSFULLY", "FAILED_TO_FETCH"
  const [isLoading, setIsLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ companyName: "", logoUrl: null });
  const appUserName = useSelector((state) => state.App.userName) || "John Smith";
  const currentRole = useSelector((state) => state.App.currentRole);
  const location = useLocation();
  const responseData = location.state;

  // Only one selected company for Document Viewer (from Blogs page)
  const selectedCompanies = useSelector((state) => state.company.selecteCompanies)
    || responseData?.selectedList || [];
  const business = selectedCompanies[0] || {};

  // Prepare API payload for lookup
  const payload = [{
    prospectBusinessId: business.prospectBusinessId,
    annualHiresOverride: business.annualHiresOverride,
  }];

  // Fetch Tax Credit Data
  useEffect(() => {
    if (!business?.prospectBusinessId) return;
    setDataFetchStatus("TRIGGERED");
    axiosInstance
      .post(`/taxCredits/lookup/v2`, payload)
      .then((response) => {
        const matched = response.data.find(
          (item) => item.prospectBusinessId === business.prospectBusinessId
        );
        if (matched) {
          dispatch(updateSelectedCompnies([{ ...business, taxCredits: [matched] }]));
          // Also update the main companies list so Blogs page has latest data!
          dispatch(updateCompanies([{ ...business, taxCredits: [matched] }]));
        }
        setDataFetchStatus("FETCHED_SUCCESSFULLY");
      })
      .catch((error) => {
        let errorMsg = error?.response?.data?.title;
        dispatch(
          showToast({
            type: "error",
            message: errorMsg || `Something went wrong. Please try again`,
          })
        );
        setDataFetchStatus("FAILED_TO_FETCH");
      });
    // Fetch company info (for logo, etc)
    const userInfo = JSON.parse(localStorage.getItem("user"));
    const companyId = userInfo?.companyId;
    if (companyId) {
      axiosInstance.get(`/companies/${companyId}`).then((response) => {
        if (response.status === 200) {
          setCompanyInfo({
            companyName: response.data.companyName,
            logoUrl: `${response.data.logoUrl}?${Date.now()}`,
          });
        }
      });
    }
    // eslint-disable-next-line
  }, []);

  const { projectedCredit, eligibleCredits } = extractTaxCreditFields(business);

  // Download Excel
  const downloadTaxCreditsAsExcel = async () => {
    setIsLoading(true);
    try {
      const jsonData = await prepareRequiredJsonToExcelDownload([business]);
      const heading = resolveVersionHeading([business]);
      const fileName = buildVersionedFileName(
        "Tax_Credit_Analysis",
        [business]
      );
      convertJsonToExcel(jsonData, fileName, { heading });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      dispatch(
        showToast({
          type: "error",
          message: "Failed to download. Please try again",
        })
      );
    }
  };

  // Download PDF (Prints the right column)
  const handleDownloadPDF = () => {
    const element = document.getElementById("pdf-content");
    // Ensure space at the bottom so content never overlaps with any footer-like elements
    const originalPaddingBottom = element.style.paddingBottom;
    element.style.paddingBottom = "64px";
    let date = dayjs(new Date()).format("DD/MM/YYYY T HH:mm:ss");
    const options = {
      margin: [0, 0, 0, 0],
      filename: `Tax Credits Analysis ${date}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, logging: false, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      // Use CSS hints and keep specific blocks together
      pagebreak: { mode: ["css", "legacy"], avoid: [".pdf-keep-together"] },
    };
    html2pdf()
      .set(options)
      .from(element)
      .save()
      .finally(() => {
        element.style.paddingBottom = originalPaddingBottom;
      });
  };

  // Navigation
  const backNavigation = () => {
    let role = currentRole?.toLowerCase?.() || "salesperson";
    navigate(`/${role}/blogs`);
    // No need to pass state, companies list is in Redux.
  };

  // Helper: format address nicely
  const formatAddress = (biz) =>
    [biz.physicalAddress, biz.physicalCity, biz.state, biz.physicalZip]
      .filter(Boolean)
      .join(", ");

  // Helper: find first phone/email if available
  const businessPhone = business.phone || "No Data Available";
  const businessEmail = business.email || "No Data Available";

  // Helper: get first tax credit description
  function getTaxCreditDescriptions(taxCreditsArr) {
    if (!Array.isArray(taxCreditsArr) || taxCreditsArr.length === 0) return [];
    return (taxCreditsArr[0].availableTaxCredits || []).map((c) => {
      const baseName = c?.name || "";
      const category = c?.category || c?.Category;
      const label = category ? `${baseName} (${category})` : baseName;
      return [label || "Unnamed credit", c?.description]
        .filter(Boolean)
        .join(": ");
    });
  }

  // Helper: Projected credit and annual sales
  const projectedCreditFormatted = projectedCredit
    ? Number(projectedCredit).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 })
    : "No Data";
  const annualSalesFormatted = business.salesVolume
    ? Number(business.salesVolume).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 })
    : "No Data";

  return (
    <div className="taxCreditWrapper">
      <Header />
      {dataFetchStatus === "FETCHED_SUCCESSFULLY" ? (
        <div className={isMobile ? "p-1" : "p-4 mx-4"}>
          <div className="p-0">
            <div className="d-flex justify-content-between px-3">
              <div className="d-flex">
                <div className="p-right">
                  <img
                    src={backNav}
                    height={isMobile ? "24px" : "36px"}
                    width={isMobile ? "24px" : "36px"}
                    onClick={backNavigation}
                    alt="Back Nav"
                  />
                </div>
                <div style={{ paddingBottom: "7px" }}>
                  <h2
                    className={`report-title my-auto ${
                      !isMobile ? "font-32" : " font-20"
                    }`}
                  >
                    Document Viewer
                  </h2>
                </div>
              </div>
              <div
                className="d-flex align-self-end gap-3"
                style={{ float: "right", padding: "4px" }}
              >
                <LoadingButton
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleDownloadPDF}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: "#FFCC33",
                    color: "#000",
                    borderColor: "#FFCC33",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#e6b800",
                      borderColor: "#e6b800",
                      boxShadow: "none",
                    },
                    "&:active": {
                      boxShadow: "none",
                    },
                    "&:focus": {
                      boxShadow: "none",
                    },
                  }}
                >
                  <img src={PDF} width="24px" height="24px" alt="download" style={{ marginRight: 8 }}/>
                  {isMobile ? "PDF" : "Download PDF"}
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={downloadTaxCreditsAsExcel}
                  loading={isLoading}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: "#FFCC33",
                    color: "#000",
                    borderColor: "#FFCC33",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#e6b800",
                      borderColor: "#e6b800",
                      boxShadow: "none",
                    },
                    "&:active": {
                      boxShadow: "none",
                    },
                    "&:focus": {
                      boxShadow: "none",
                    },
                  }}
                >
                  <img src={CSV} width="24px" height="24px" alt="download excel" style={{ marginRight: 8 }}/>
                  {isMobile ? "Excel" : "Download Excel"}
                </LoadingButton>
              </div>
            </div>
          </div>
          <div className="tcaviewer-container" style={{ display: "flex", gap: 0, marginTop: 24, background: "#fff", borderRadius: 16, boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
            {/* Left Summary Card */}
            <div className="tcaviewer-left" style={{ flex: "1 1 350px", minWidth: 320, maxWidth: 400, background: "#fafbfc", borderRight: "1px solid #e5e7f3", padding: "36px 32px" }}>
              <div className="tcaviewer-summary">
                <div style={{ fontSize: 18, color: "#888", marginBottom: 10 }}>Summary</div>
                <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 3 }}>{business.businessName}</div>
                <div style={{ color: "#444", marginBottom: 10 }}>{formatAddress(business)}</div>
                <div style={{ fontSize: 15, marginBottom: 4 }}>
                  <span role="img" aria-label="category" style={{ marginRight: 4 }}>📄</span> Category: {business.siC01Description || "No Data"}
                </div>
                <div style={{ fontSize: 15, marginBottom: 4 }}>
                  <span role="img" aria-label="employees" style={{ marginRight: 4 }}>👤</span> Employees: {business.locationEmployeesCount || "No Data"}
                </div>
                <div style={{ fontSize: 15, marginBottom: 16 }}>
                  <span role="img" aria-label="hires" style={{ marginRight: 4 }}>👤</span> Annual Hires: {business.annualHires || "~"}
                </div>
                <div style={{ fontSize: 16, color: "#666", margin: "18px 0 0 0" }}>Projected Credit:</div>
                <div style={{ fontWeight: 700, fontSize: 25, color: "#222" }}>{projectedCreditFormatted}</div>
                <div style={{ marginTop: 14, fontWeight: 500, color: "#666" }}>Eligible Credits:</div>
                <div style={{ marginTop: 6, display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {Array.isArray(eligibleCredits) && eligibleCredits.length > 0
                    ? eligibleCredits.map((c, i) => (
                        <span
                          key={i}
                          className="eligible-credit-chip"
                        >
                          {c}
                        </span>
                      ))
                    : <span style={{ color: "#aaa" }}>No Data</span>
                  }
                </div>
              </div>
            </div>
            {/* Right Document Card */}
            <div className="tcaviewer-right" style={{ flex: "2 1 0", padding: "0", minWidth: 0, background: "#fff" }}>
              <div id="pdf-content" style={{ borderRadius: 0, background: "white", paddingBottom: 64 }}>
                <div style={{ background: "#444549", color: "#fff", borderRadius: "0 16px 0 0", padding: "22px 32px 18px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 23, fontWeight: 600, letterSpacing: "-1px" }}>Tax Credit Analysis</div>
                    <div style={{ fontSize: 14, marginTop: 8 }}>Provided by: {appUserName}</div>
                  </div>
                  {/* Powered by canary, styled as in previous code, matching image 2 */}
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                    minWidth: 120,
                  }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: 15,
                      letterSpacing: 1,
                      color: "#fff",
                      marginBottom: 2,
                      textTransform: "uppercase",
                    }}>
                      POWERED BY:
                    </div>
                    <img
                      src={canaryLogo}
                      alt="canary"
                      style={{
                        height: 28,
                        width: "auto",
                        marginBottom: 4,
                      }}
                    />
                    <div style={{ fontSize: 14, color: "#fff" }}>
                      {dayjs().format("M/D/YYYY")}
                    </div>
                  </div>
                </div>
                <div style={{ padding: "2.5rem 2.5rem 1.5rem 2.5rem" }}>
                  {/* Business Profile */}
                  <div style={{ color: "#888", fontSize: 14, marginBottom: 10 }}>Business Profile</div>
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", rowGap: 8, columnGap: 16, fontSize: 16 }}>
                    <div>Name</div>
                    <div style={{ fontWeight: 600 }}>{business.businessName}</div>
                    <div>Category</div>
                    <div>{business.siC01Description || "No Data"}</div>
                    <div>Location</div>
                    <div>{formatAddress(business)}</div>
                    <div>Employees</div>
                    <div>{business.locationEmployeesCount || "No Data"}</div>
                    <div>Annual hires</div>
                    <div>{business.annualHires || "~"}</div>
                    <div>Phone</div>
                    <div>{businessPhone}</div>
                    <div>Email</div>
                    <div>{businessEmail}</div>
                  </div>
                  <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "30px 0 18px 0" }} />
                  {/* Tax Credit Analysis */}
                  <div style={{ color: "#888", fontSize: 14, marginBottom: 10 }}>Tax Credit Analysis</div>
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", rowGap: 10, columnGap: 16, fontSize: 16 }}>
                    <div>Projected Credit</div>
                    <div style={{ fontWeight: 700 }}>{projectedCreditFormatted}</div>
                    <div>Annual Sales</div>
                    <div>{annualSalesFormatted}</div>
                    <div style={{ alignSelf: "start" }}>Credits Identified</div>
                    <div>
                      {getTaxCreditDescriptions(business.taxCredits).length > 0
                        ? getTaxCreditDescriptions(business.taxCredits).map((desc, i) => (
                            <div key={i} style={{ marginBottom: 10 }}>
                              <span style={{ fontWeight: 600 }}>{desc.split(":")[0]}</span>
                              <div style={{ fontSize: 15, color: "#333", marginLeft: 2 }}>
                                {desc.split(":").slice(1).join(":").trim()}
                              </div>
                            </div>
                          ))
                        : <span style={{ color: "#aaa" }}>No Data</span>
                      }
                    </div>
                  </div>
                  {/* Disclaimer */}
                  <div className="pdf-keep-together" style={{ marginTop: 28, fontSize: 13, color: "#777", breakInside: "avoid", pageBreakInside: "avoid", pageBreakBefore: "avoid", pageBreakAfter: "avoid", position: "relative", zIndex: 2, background: "#fff" }}>
                    <b>Disclaimer:</b> Projected savings are based on estimates related to employee hiring and retention, reflecting adjustments to annual hire data. State credits use a 35% participation rate, while federal credits apply a 20% factor. Actual results may vary.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : dataFetchStatus === "TRIGGERED" ? (
        <CircularProgress
          size={24}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            marginTop: "-12px",
            marginLeft: "-12px",
          }}
        />
      ) : null}
    </div>
  );
};

export default TaxCreditAnalysis;
